import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Login from './pages/Login';
import CourseDesigner from './pages/CourseDesigner';
import ParcoursList from './pages/ParcoursList';
import CourseList from './pages/CourseList';
import BlocDetails from './pages/BlocDetails';
import CategoryDetails from './pages/CategoryDetails';
import CourseDetails from './pages/CourseDetails';
import MarketingPage from './pages/MarketingPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import EnrollmentManagement from './pages/EnrollmentManagement';
import OrganizationManagement from './pages/OrganizationManagement';
import ApiDocsPage from './pages/ApiDocsPage';
import AdminOSLayout from './components/layout/AdminOSLayout';
import LearnersPage from './pages/LearnersPage';
import MyOFPage from './pages/MyOFPage';
import BlockEvaluationPage from './pages/BlockEvaluationPage';
import LearnerLayout from './components/layout/LearnerLayout';
import LearnerDashboard from './pages/learner/LearnerDashboard';
import MyParcours from './pages/learner/MyParcours';
import LearnerParcoursDetails from './pages/learner/LearnerParcoursDetails';
import LearnerBlocDetails from './pages/learner/LearnerBlocDetails';
import LearnerCategoryDetails from './pages/learner/LearnerCategoryDetails';
import CoursePlayer from './pages/learner/CoursePlayer';
import ProfilePage from './pages/learner/ProfilePage';
import LearnerSurveys from './pages/learner/LearnerSurveys';
import LearnerBlockEvaluations from './pages/learner/LearnerBlockEvaluations';
import LearnerEvaluationPage from './pages/learner/LearnerEvaluationPage';
import ChatPage from './pages/learner/ChatPage';
import EmailsPage from './pages/learner/EmailsPage';
import LearnerListPage from './pages/admin/apprenants/LearnerListPage';
import LearnerDetailsPage from './pages/admin/apprenants/LearnerDetailsPage';
import LearnerList from './pages/reports/LearnerList';
import ReportConfig from './pages/reports/ReportConfig';
import QuestionsPage from './pages/questions/QuestionsPage';
import TrainerOSLayout from './components/layout/TrainerOSLayout';
import TrainerApprenantsPage from './pages/trainer/TrainerApprenantsPage';
import TrainersPage from './pages/admin/trainers/TrainersPage';
import StatisticsPage from './pages/admin/statistics/StatisticsPage';
import SatisfactionPage from './pages/admin/satisfaction/SatisfactionPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import InactivityLogout from './components/common/InactivityLogout';
import MediaLibraryPage from './pages/admin/media/MediaLibraryPage';
import ErrorReporterPage from './pages/admin/errors/ErrorReporterPage';
import ContractManagementPage from './pages/admin/ContractManagementPage';
import CourseReviewPage from './pages/admin/CourseReviewPage';
import AdminChatPage from './pages/admin/AdminChatPage';
import OrganizationSecurity from './pages/admin/OrganizationSecurity';
import CGUPage from './pages/legal/CGUPage';
import RGPDPage from './pages/legal/RGPDPage';
import LearnerRulesPage from './pages/legal/LearnerRulesPage';
import RsePage from './pages/legal/RsePage';
import PatchNotePage from './pages/PatchNotePage';
import MobileGuard from './components/common/MobileGuard';
import React, { useContext } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import SnackbarUtilsConfigurator from './components/SnackbarUtilsConfigurator';

const theme = createTheme();

// Component to track global time spent on platform
const GlobalTimeTracker = () => {
    const auth = useContext(AuthContext);
    const lastActivityRef = React.useRef(Date.now());

    React.useEffect(() => {
        if (!auth?.user) return;

        const updateTime = async () => {
            // Only update if user has been active in the last minute
            if (Date.now() - lastActivityRef.current < 60000) {
                try {
                    const api = (await import('./services/api')).default;
                    await api.post('/api/users/heartbeat', {
                        timeSpent: 60,
                        loginLogId: auth?.loginLogId
                    });
                } catch (err) {
                    console.error("Error updating time", err);
                }
            }
        };

        const interval = setInterval(updateTime, 60000); // Every minute

        const handleActivity = () => {
            lastActivityRef.current = Date.now();
        };

        // Handle window close/refresh
        const handleBeforeUnload = async () => {
            const loginLogId = localStorage.getItem('loginLogId');
            if (loginLogId) {
                // Use sendBeacon for reliable delivery during page unload
                const blob = new Blob([JSON.stringify({ loginLogId })], { type: 'application/json' });
                const token = localStorage.getItem('token');

                // Fallback to synchronous fetch if sendBeacon not available
                if (navigator.sendBeacon) {
                    navigator.sendBeacon('/api/users/logout', blob);
                } else {
                    try {
                        const api = (await import('./services/api')).default;
                        await api.post('/api/users/logout', { loginLogId });
                    } catch (err) {
                        console.error("Error recording logout on unload", err);
                    }
                }
            }
        };

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('scroll', handleActivity);
        window.addEventListener('touchstart', handleActivity);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            clearInterval(interval);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            window.removeEventListener('touchstart', handleActivity);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [auth?.user, auth?.loginLogId]);

    return null;
};

import LottieLoading from './components/common/LottieLoading';

interface PrivateRouteProps {
    children: React.ReactElement;
    roles?: string[];
}

const PrivateRoute = ({ children, roles }: PrivateRouteProps) => {
    const auth = useContext(AuthContext);

    if (auth?.loading) return <LottieLoading message="Chargement de l'application..." animationType="learner" />;

    if (!auth?.user) {
        return <Navigate to="/login" />;
    }

    // Force learner to accept rules if not done yet
    if (auth.user.role === 'learner' && !auth.user.hasAcceptedRules && window.location.pathname !== '/learner-rules') {
        return <Navigate to="/learner-rules" />;
    }

    if (roles && !roles.includes(auth.user.role)) {
        return <Navigate to="/dashboard" />;
    }

    return children;
};

// Wrapper to route Questions page based on user role
const QuestionsRouteWrapper = () => {
    const auth = useContext(AuthContext);
    const Layout = auth?.user?.role === 'trainer' ? TrainerOSLayout : AdminOSLayout;
    return (
        <Layout>
            <QuestionsPage />
        </Layout>
    );
};

// Wrapper for Trainer Apprenants page
const TrainerApprenantsRouteWrapper = () => {
    return (
        <TrainerOSLayout>
            <TrainerApprenantsPage />
        </TrainerOSLayout>
    );
};

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <ToastProvider>
                    <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <SnackbarUtilsConfigurator />
                        <GlobalTimeTracker />
                        <InactivityLogout />
                        <Router>
                            <Routes>
                                <Route path="/newPassword" element={<SetPasswordPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/login" element={<Login />} />

                                <Route path="/of-cgu" element={<CGUPage />} />
                                <Route path="/rgpd" element={<RGPDPage />} />
                                <Route path="/learner-rules" element={<LearnerRulesPage />} />
                                <Route path="/rse" element={<RsePage />} />
                                <Route path="/patchnotes" element={<PatchNotePage />} />

                                <Route
                                    path="/questions"
                                    element={
                                        <PrivateRoute>
                                            <QuestionsRouteWrapper />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/parcours/:parcoursId/bloc/:blocId"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <BlocDetails />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />

                                {/* Learner Administration Routes */}
                                <Route
                                    path="/admin/apprenants"
                                    element={
                                        <PrivateRoute>
                                            <LearnerListPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/admin/apprenants/:id"
                                    element={
                                        <PrivateRoute>
                                            <LearnerDetailsPage />
                                        </PrivateRoute>
                                    }
                                />


                                {/* Protected Routes with AdminOSLayout */}
                                <Route
                                    path="/dashboard"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/courses"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Cours">
                                                    <CourseList />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/marketing"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <MarketingPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <SettingsPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/parcours"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Parcours">
                                                    <ParcoursList />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/parcours/:id"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <CourseDesigner />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/category/:categoryId"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <CategoryDetails />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/parcours/:parcoursId/bloc/:blocId"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <BlocDetails />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />

                                {/* Learner Administration Routes */}
                                <Route
                                    path="/admin/apprenants"
                                    element={
                                        <PrivateRoute>
                                            <LearnerListPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/admin/apprenants/:id"
                                    element={
                                        <PrivateRoute>
                                            <LearnerDetailsPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/course/:courseId"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <CourseDetails />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/users"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <UsersPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learners"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <LearnersPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/mon-of"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Mon OF">
                                                    <MyOFPage />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/block-evaluation"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <BlockEvaluationPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/api-docs"
                                    element={
                                        <PrivateRoute>
                                            <ApiDocsPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/enrollment"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <EnrollmentManagement />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/organizations"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <OrganizationManagement />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/course-review"
                                    element={
                                        <PrivateRoute roles={['super-admin']}>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Cours à Réviser">
                                                    <CourseReviewPage />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/reports"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <LearnerList />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/reports/:learnerId"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <ReportConfig />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/media-library"
                                    element={
                                        <PrivateRoute roles={['super-admin', 'of-admin']}>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Médiathèque">
                                                    <MediaLibraryPage />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/admin/error-reporter"
                                    element={
                                        <PrivateRoute roles={['super-admin', 'of-admin']}>
                                            <AdminOSLayout>
                                                <ErrorReporterPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/contracts"
                                    element={
                                        <PrivateRoute roles={['super-admin']}>
                                            <AdminOSLayout>
                                                <MobileGuard appName="Contrats">
                                                    <ContractManagementPage />
                                                </MobileGuard>
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/admin/chat"
                                    element={
                                        <PrivateRoute roles={['super-admin', 'of-admin']}>
                                            <AdminOSLayout>
                                                <AdminChatPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/admin/security"
                                    element={
                                        <PrivateRoute roles={['super-admin', 'of-admin']}>
                                            <AdminOSLayout>
                                                <OrganizationSecurity />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />

                                {/* Learner Routes */}
                                <Route path="/learner" element={<Navigate to="/learner/dashboard" replace />} />
                                <Route
                                    path="/learner/dashboard"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerDashboard />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/parcours"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <MyParcours />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/parcours/:id"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerParcoursDetails />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/parcours/:parcoursId/bloc/:blocId"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerBlocDetails />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/category/:categoryId"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerCategoryDetails />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/course/:courseId"
                                    element={
                                        <PrivateRoute>
                                            <CoursePlayer />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/profile"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <ProfilePage />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/surveys"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerSurveys />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/evaluations"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <LearnerBlockEvaluations />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/chat"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <ChatPage />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/messages"
                                    element={
                                        <Navigate to="/learner/chat" replace />
                                    }
                                />
                                <Route
                                    path="/learner/evaluations/:evaluationId"
                                    element={
                                        <PrivateRoute>
                                            <LearnerEvaluationPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/learner/emails"
                                    element={
                                        <PrivateRoute>
                                            <LearnerLayout>
                                                <EmailsPage />
                                            </LearnerLayout>
                                        </PrivateRoute>
                                    }
                                />

                                {/* Trainers Management (OF-Admin) */}
                                <Route
                                    path="/statistics"
                                    element={
                                        <PrivateRoute roles={['super-admin']}>
                                            <MobileGuard appName="Statistiques">
                                                <StatisticsPage />
                                            </MobileGuard>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/satisfaction"
                                    element={
                                        <PrivateRoute roles={['super-admin', 'of-admin']}>
                                            <SatisfactionPage />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/trainers"
                                    element={
                                        <PrivateRoute>
                                            <AdminOSLayout>
                                                <TrainersPage />
                                            </AdminOSLayout>
                                        </PrivateRoute>
                                    }
                                />

                                {/* Trainer Routes */}
                                <Route
                                    path="/trainer/apprenants"
                                    element={
                                        <PrivateRoute>
                                            <TrainerApprenantsRouteWrapper />
                                        </PrivateRoute>
                                    }
                                />

                                {/* Redirect root to dashboard */}
                                <Route path="/" element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </Router>
                    </SnackbarProvider>
                </ToastProvider>
            </AuthProvider>
        </ThemeProvider >
    );
}

export default App;
